import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { Pagination } from '@/common/types';
import { API_URL, ILinkedEmployeeInfoDto } from '@/dtos';
import { EmployeeStatus } from '@/modules/employee/employee.constants';
import { EmployeeAddress, EmployeeAddressPayload, StatusNames } from '@/modules/employee/employee.types';
import { OrganizationStatus } from '@/modules/employee/Employee/Employee.types';
import { getFullStatusName } from '@/modules/employee/hooks/useEmployeesList.query';

const DEFAULT_PAGINATION_PARAMS = {
  size: 100,
  page: 0,
};

export const GET_AUTOMATICALLY_SEARCHED_ACCOUNTS_QUERY_KEY = 'GET_AUTOMATICALLY_SEARCHED_ACCOUNTS_QUERY_KEY';
export const GET_MANUALLY_SEARCHED_ACCOUNTS_QUERY_KEY = 'GET_MANUALLY_SEARCHED_ACCOUNTS_QUERY_KEY';

export interface LinkedAccountPayload {
  employee_id?: number;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  ssn?: string;
  employer_employee_id?: string;
  partner_employee_id?: string;
  employment_status?: OrganizationStatus;
  employer_name?: string;
  address?: EmployeeAddressPayload;
  is_primary_linked_account?: boolean;
  preferred_mailing?: boolean;
  preferred_physical?: boolean;
  linked_employees?: LinkedAccountPayload[];
}

export interface LinkedAccount {
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  dateOfBirth?: string;
  ssn?: string;
  employerEmployeeId?: string;
  partnerEmployeeId?: string;
  employmentStatus?: EmployeeStatus;
  employerName?: string;
  isPrimaryLinkedAccount?: boolean;
  address?: EmployeeAddress;
  linkedEmployees?: LinkedAccount[];
}

export const formatEmployeeData = (data?: ILinkedEmployeeInfoDto[], employeeId?: string): LinkedAccount[] =>
  data
    ?.map((item) => {
      const formattedItem: LinkedAccount = {
        employeeId: toString(item?.employee_id),
        firstName: item?.first_name,
        lastName: item?.last_name,
        fullName: `${item?.first_name || ''} ${item?.last_name || ''}`.trim(),
        dateOfBirth: item?.date_of_birth as unknown as string,
        ssn: item?.ssn,
        employerEmployeeId: toString(item?.employer_employee_id),
        partnerEmployeeId: toString(item?.partner_employee_id),
        ...(item?.employment_status
          ? {
              employmentStatus: getFullStatusName(
                item?.employment_status as keyof typeof StatusNames
              ) as unknown as EmployeeStatus,
            }
          : {}),
        employerName: item?.employer_name,
        isPrimaryLinkedAccount: item?.is_primary_linked_account,
        ...(item?.address
          ? ({
              address: {
                id: toString(item.address?.id),
                city: item.address?.city,
                line1: item.address?.line1,
                line2: item.address?.line2,
                line3: item.address?.line3,
                line4: item.address?.line4,
                zipcode: item.address?.zipcode,
                countryId: toString(item.address?.country_id),
                addressType: item.address?.address_type,
                preferredMailing: item.address?.preferred_mailing,
                preferredPhysical: item.address?.preferred_physical,
              },
            } as EmployeeAddress)
          : {}),
        ...(item?.linked_employees?.length
          ? {
              linkedEmployees: formatEmployeeData(item.linked_employees, employeeId),
            }
          : {}),
      };
      return formattedItem;
    })
    ?.filter((item) => item.employeeId !== employeeId) || [];

export const useAutomaticallySearchedAccountsQuery = (employeeId?: string, enabled?: boolean) => {
  const { data, isLoading, isSuccess } = useQuery(
    [GET_AUTOMATICALLY_SEARCHED_ACCOUNTS_QUERY_KEY, employeeId, enabled],
    () =>
      api.get<Pagination<ILinkedEmployeeInfoDto>>(API_URL.EMPLOYEES_LINKAGE_SEARCH, {
        params: {
          ...DEFAULT_PAGINATION_PARAMS,
          employee_id: employeeId,
        },
      }),
    { enabled: Boolean(employeeId && enabled), retry: false }
  );

  const formattedData = useMemo(() => {
    if (Array.isArray(data?.data?.content)) {
      return formatEmployeeData(data?.data?.content, employeeId);
    }

    return [] as LinkedAccount[];
  }, [data, employeeId]);

  return {
    formattedData,
    data: data?.data?.content || [],
    isLoading,
    isSuccess,
  };
};

export const useManuallySearchedAccountsQuery = (employeeId: string, search?: string, enabled?: boolean) => {
  const { data, isLoading, isSuccess } = useQuery(
    [GET_MANUALLY_SEARCHED_ACCOUNTS_QUERY_KEY, employeeId, search, enabled],
    () =>
      api.get<Pagination<LinkedAccountPayload>>(API_URL.EMPLOYEES_LINKAGE_SEARCH, {
        params: {
          ...DEFAULT_PAGINATION_PARAMS,
          search_value: search,
        },
      }),
    { enabled: Boolean(employeeId && search && search.length >= 3 && enabled), retry: false }
  );

  const formattedData = useMemo(() => {
    if (Array.isArray(data?.data?.content)) {
      // @ts-ignore
      return formatEmployeeData(data?.data?.content as ILinkedEmployeeInfoDto[], employeeId);
    }

    return [] as LinkedAccount[];
  }, [data, employeeId]);

  return {
    formattedData,
    isLoading,
    isSuccess,
  };
};
